import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { FaTags, FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import depositosApi from '../../services/api/depositosApi';
import 'react-toastify/dist/ReactToastify.css';

const TiposDeposito = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    activo: true,
  });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await depositosApi.getTiposDeposito(false);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar tipos de depósito');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      activo: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        codigo: formData.codigo.trim(),
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || null,
        activo: formData.activo,
      };
      if (editing) {
        await depositosApi.updateTipoDeposito(editing.id, payload);
        toast.success('Tipo de depósito actualizado');
      } else {
        await depositosApi.createTipoDeposito(payload);
        toast.success('Tipo de depósito creado');
      }
      setShowModal(false);
      resetForm();
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      codigo: row.codigo || '',
      nombre: row.nombre || '',
      descripcion: row.descripcion || '',
      activo: row.activo !== undefined ? row.activo : true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de depósito? Solo es posible si no tiene depósitos.')) {
      return;
    }
    try {
      await depositosApi.deleteTipoDeposito(id);
      toast.success('Tipo de depósito eliminado');
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtered = items.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      r.codigo?.toLowerCase().includes(q) ||
      r.nombre?.toLowerCase().includes(q) ||
      (r.descripcion && r.descripcion.toLowerCase().includes(q))
    );
  });

  return (
    <Container fluid className="p-4">
      <ToastContainer />
      <Row className="mb-3">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <FaTags size={24} className="text-primary" />
                <div>
                  <h4 className="mb-0">Tipos de depósito</h4>
                  <small className="text-muted">Catálogo usado al registrar depósitos corporativos</small>
                </div>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                <FaPlus className="me-2" />
                Nuevo
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Buscar</Form.Label>
                <div className="position-relative">
                  <FaSearch className="position-absolute text-muted" style={{ left: 10, top: 11 }} />
                  <Form.Control
                    type="text"
                    placeholder="Código o nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 32 }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {loading && items.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">No hay registros</Alert>
          ) : (
            <Table responsive striped hover size="sm">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th style={{ width: 120 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>{r.codigo}</td>
                    <td>{r.nombre}</td>
                    <td>{r.descripcion || '—'}</td>
                    <td>
                      {r.activo ? (
                        <Badge bg="success">Activo</Badge>
                      ) : (
                        <Badge bg="secondary">Inactivo</Badge>
                      )}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleEdit(r)}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar tipo de depósito' : 'Nuevo tipo de depósito'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Código *</Form.Label>
                  <Form.Control
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleInputChange}
                    required
                    disabled={!!editing}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control name="nombre" value={formData.nombre} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="activo-td"
              name="activo"
              label="Activo"
              checked={formData.activo}
              onChange={handleInputChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default TiposDeposito;
